// src/pages/legal/CookiePolicy.tsx

import React from "react";
import { Link } from "react-router-dom";

import "./LegalPages.css";

// =====================================================
// COOKIE POLICY
// =====================================================

const CookiePolicy: React.FC = () => {
  return (
    <main className="legal-page">

      {/* ================================================= */}
      {/* HERO                                              */}
      {/* ================================================= */}

      <section className="legal-hero">

        <div className="legal-hero-glow" />

        <div className="legal-hero-content">

          {/* ============================================= */}
          {/* BREADCRUMB                                    */}
          {/* ============================================= */}

          <nav
            className="legal-breadcrumb"
            aria-label="Breadcrumb"
          >
            <Link to="/">
              P-ARENA
            </Link>

            <span>/</span>

            <span>
              Legal
            </span>

            <span>/</span>

            <strong aria-current="page">
              Cookie Policy
            </strong>
          </nav>

          {/* ============================================= */}
          {/* EMBLEM                                        */}
          {/* ============================================= */}

          <div className="legal-hero-emblem">
            ♣
          </div>

          <p className="legal-eyebrow">
            P-ARENA LEGAL
          </p>

          <h1>
            Cookie Policy
          </h1>

          <div className="legal-divider">
            <span />
            <strong>◆</strong>
            <span />
          </div>

          <p className="legal-hero-description">
            This Cookie Policy explains how P-ARENA
            may use cookies, browser storage, and
            similar technologies to operate, secure,
            and improve the platform.
          </p>

          <div className="legal-last-updated">
            Last Updated: August 2026
          </div>

        </div>

      </section>

      {/* ================================================= */}
      {/* PAGE NAVIGATION                                   */}
      {/* ================================================= */}

      <nav
        className="legal-section-nav"
        aria-label="Cookie Policy sections"
      >
        <div className="legal-section-nav-inner">

          <a href="#introduction">
            Introduction
          </a>

          <a href="#what-are-cookies">
            What Are Cookies?
          </a>

          <a href="#technologies">
            Technologies We Use
          </a>

          <a href="#purposes">
            Why We Use Them
          </a>

          <a href="#types">
            Cookie Types
          </a>

          <a href="#local-storage">
            Local Storage
          </a>

          <a href="#third-parties">
            Third Parties
          </a>

          <a href="#choices">
            Your Choices
          </a>

          <a href="#changes">
            Changes
          </a>

          <a href="#contact">
            Contact
          </a>

        </div>
      </nav>

      {/* ================================================= */}
      {/* CONTENT                                           */}
      {/* ================================================= */}

      <div className="legal-content">

        {/* ================================================= */}
        {/* INTRODUCTION                                      */}
        {/* ================================================= */}

        <section
          id="introduction"
          className="legal-section"
        >

          <div className="legal-section-header">

            <span className="legal-section-number">
              01
            </span>

            <div>
              <p>
                OVERVIEW
              </p>

              <h2>
                Introduction
              </h2>
            </div>

          </div>

          <div className="legal-copy">

            <p>
              This Cookie Policy explains how{" "}
              <strong>P-ARENA</strong> may use
              cookies, browser storage, session
              storage, and similar technologies
              when you access or use the P-ARENA
              platform.
            </p>

            <p>
              These technologies can help websites
              and applications provide essential
              functionality, maintain sessions,
              remember preferences, protect
              accounts, and improve the user
              experience.
            </p>

            <p>
              This Cookie Policy should be read
              together with our{" "}
              <Link
                to="/privacy"
                className="legal-inline-link"
              >
                Privacy Policy
              </Link>.
            </p>

          </div>

        </section>

        {/* ================================================= */}
        {/* WHAT ARE COOKIES                                  */}
        {/* ================================================= */}

        <section
          id="what-are-cookies"
          className="legal-section"
        >

          <div className="legal-section-header">

            <span className="legal-section-number">
              02
            </span>

            <div>
              <p>
                BASICS
              </p>

              <h2>
                What Are Cookies?
              </h2>
            </div>

          </div>

          <div className="legal-copy">

            <p>
              Cookies are small pieces of data that
              a website may place on your browser
              or device when you visit or interact
              with the website.
            </p>

            <p>
              Cookies can perform different
              functions. For example, they may help
              maintain a login session, remember a
              preference, support security
              features, or help a service understand
              whether its features are functioning
              correctly.
            </p>

            <p>
              Some cookies exist only for a browsing
              session and are removed when the
              session ends. Others may remain for a
              longer period unless they expire or
              are removed through browser settings.
            </p>

          </div>

        </section>

        {/* ================================================= */}
        {/* TECHNOLOGIES                                      */}
        {/* ================================================= */}

        <section
          id="technologies"
          className="legal-section"
        >

          <div className="legal-section-header">

            <span className="legal-section-number">
              03
            </span>

            <div>
              <p>
                BROWSER TECHNOLOGIES
              </p>

              <h2>
                Technologies P-ARENA May Use
              </h2>
            </div>

          </div>

          <div className="legal-copy">

            <p>
              Depending on the features being used,
              P-ARENA may use one or more browser
              technologies to maintain application
              functionality and user experience.
            </p>

          </div>

          <div className="legal-card-grid">

            {/* COOKIES */}

            <article className="legal-info-card">

              <span className="legal-card-number">
                01
              </span>

              <h3>
                Cookies
              </h3>

              <p>
                Small data files that may be stored
                by your browser to support website
                and account functionality.
              </p>

              <ul>
                <li>
                  Session management
                </li>

                <li>
                  Security functionality
                </li>

                <li>
                  User preferences
                </li>

                <li>
                  Platform functionality
                </li>
              </ul>

            </article>

            {/* LOCAL STORAGE */}

            <article className="legal-info-card">

              <span className="legal-card-number">
                02
              </span>

              <h3>
                Local Storage
              </h3>

              <p>
                Browser local storage may be used
                to retain information needed by the
                application between browser
                sessions.
              </p>

              <ul>
                <li>
                  Application preferences
                </li>

                <li>
                  Interface settings
                </li>

                <li>
                  Application state
                </li>

                <li>
                  Other locally stored settings
                </li>
              </ul>

            </article>

            {/* SESSION STORAGE */}

            <article className="legal-info-card">

              <span className="legal-card-number">
                03
              </span>

              <h3>
                Session Storage
              </h3>

              <p>
                Session storage may temporarily
                retain information needed during a
                particular browser session.
              </p>

              <ul>
                <li>
                  Temporary application state
                </li>

                <li>
                  Navigation state
                </li>

                <li>
                  Temporary session information
                </li>

                <li>
                  Game-flow information
                </li>
              </ul>

            </article>

            {/* SIMILAR TECHNOLOGIES */}

            <article className="legal-info-card">

              <span className="legal-card-number">
                04
              </span>

              <h3>
                Similar Technologies
              </h3>

              <p>
                As P-ARENA develops, other
                technologies may be used where
                reasonably necessary to provide,
                secure, or improve the platform.
              </p>

              <ul>
                <li>
                  Security technologies
                </li>

                <li>
                  Diagnostic technologies
                </li>

                <li>
                  Performance technologies
                </li>

                <li>
                  Platform-support technologies
                </li>
              </ul>

            </article>

          </div>

        </section>

        {/* ================================================= */}
        {/* PURPOSES                                          */}
        {/* ================================================= */}

        <section
          id="purposes"
          className="legal-section"
        >

          <div className="legal-section-header">

            <span className="legal-section-number">
              04
            </span>

            <div>
              <p>
                PURPOSE
              </p>

              <h2>
                Why We Use These Technologies
              </h2>
            </div>

          </div>

          <div className="legal-copy">

            <p>
              P-ARENA may use cookies and similar
              browser technologies for purposes
              related to operating and protecting
              the platform.
            </p>

          </div>

          <div className="legal-purpose-list">

            <article>

              <span>
                01
              </span>

              <div>

                <h3>
                  Authentication
                </h3>

                <p>
                  Help maintain account and login
                  functionality so that authenticated
                  users can access appropriate
                  P-ARENA features.
                </p>

              </div>

            </article>

            <article>

              <span>
                02
              </span>

              <div>

                <h3>
                  Security
                </h3>

                <p>
                  Support account security, session
                  integrity, abuse prevention, and
                  other measures designed to protect
                  users and the platform.
                </p>

              </div>

            </article>

            <article>

              <span>
                03
              </span>

              <div>

                <h3>
                  Application Functionality
                </h3>

                <p>
                  Maintain information needed for
                  navigation, game flows, account
                  features, and other platform
                  functionality.
                </p>

              </div>

            </article>

            <article>

              <span>
                04
              </span>

              <div>

                <h3>
                  Preferences
                </h3>

                <p>
                  Remember certain choices or
                  settings so users do not
                  necessarily need to select them
                  again each time they use the
                  platform.
                </p>

              </div>

            </article>

            <article>

              <span>
                05
              </span>

              <div>

                <h3>
                  Performance &amp; Diagnostics
                </h3>

                <p>
                  Help identify technical problems,
                  understand whether platform
                  features are working correctly,
                  and improve reliability.
                </p>

              </div>

            </article>

          </div>

        </section>

        {/* ================================================= */}
        {/* COOKIE TYPES                                      */}
        {/* ================================================= */}

        <section
          id="types"
          className="legal-section"
        >

          <div className="legal-section-header">

            <span className="legal-section-number">
              05
            </span>

            <div>
              <p>
                CATEGORIES
              </p>

              <h2>
                Types of Cookies
              </h2>
            </div>

          </div>

          <div className="legal-copy">

            <p>
              Cookies and similar technologies can
              generally be grouped according to
              their purpose. The categories
              applicable to P-ARENA may change as
              the platform develops.
            </p>

          </div>

          <div className="legal-card-grid">

            <article className="legal-info-card">

              <span className="legal-card-number">
                01
              </span>

              <h3>
                Strictly Necessary
              </h3>

              <p>
                Technologies necessary for core
                platform functionality, security,
                authentication, or session
                management.
              </p>

              <ul>
                <li>
                  Login functionality
                </li>

                <li>
                  Account security
                </li>

                <li>
                  Session management
                </li>

                <li>
                  Core platform operation
                </li>
              </ul>

            </article>

            <article className="legal-info-card">

              <span className="legal-card-number">
                02
              </span>

              <h3>
                Functional
              </h3>

              <p>
                Technologies that may help remember
                choices and provide a more
                consistent user experience.
              </p>

              <ul>
                <li>
                  User preferences
                </li>

                <li>
                  Interface settings
                </li>

                <li>
                  Remembered application state
                </li>
              </ul>

            </article>

            <article className="legal-info-card">

              <span className="legal-card-number">
                03
              </span>

              <h3>
                Performance
              </h3>

              <p>
                Technologies that may help us
                understand technical performance
                and diagnose problems with the
                platform.
              </p>

              <ul>
                <li>
                  Error information
                </li>

                <li>
                  Performance information
                </li>

                <li>
                  Technical diagnostics
                </li>
              </ul>

            </article>

            <article className="legal-info-card">

              <span className="legal-card-number">
                04
              </span>

              <h3>
                Analytics or Advertising
              </h3>

              <p>
                If P-ARENA introduces analytics,
                advertising, or similar
                technologies in the future, this
                Cookie Policy may be updated to
                describe those technologies and
                available user choices.
              </p>

            </article>

          </div>

        </section>

        {/* ================================================= */}
        {/* LOCAL STORAGE                                     */}
        {/* ================================================= */}

        <section
          id="local-storage"
          className="legal-section"
        >

          <div className="legal-section-header">

            <span className="legal-section-number">
              06
            </span>

            <div>
              <p>
                APPLICATION STORAGE
              </p>

              <h2>
                Local &amp; Session Storage
              </h2>
            </div>

          </div>

          <div className="legal-highlight">

            <span className="legal-highlight-icon">
              ♣
            </span>

            <div>

              <small>
                IMPORTANT
              </small>

              <h3>
                Cookies Are Not the Only Browser
                Storage Technology
              </h3>

              <p>
                Web applications can also use local
                storage and session storage to
                maintain information needed by the
                application.
              </p>

            </div>

          </div>

          <div className="legal-copy">

            <p>
              Local storage generally allows a
              browser to retain information after a
              browser session ends. Session storage
              generally retains information only
              for the relevant browser session.
            </p>

            <p>
              P-ARENA may use these technologies
              where needed to maintain application
              state, preferences, authentication
              information, or other platform
              functionality.
            </p>

            <p>
              Removing locally stored information
              may sign you out, reset preferences,
              or affect certain P-ARENA features.
            </p>

          </div>

        </section>

        {/* ================================================= */}
        {/* THIRD-PARTY TECHNOLOGIES                          */}
        {/* ================================================= */}

        <section
          id="third-parties"
          className="legal-section"
        >

          <div className="legal-section-header">

            <span className="legal-section-number">
              07
            </span>

            <div>
              <p>
                EXTERNAL SERVICES
              </p>

              <h2>
                Third-Party Technologies
              </h2>
            </div>

          </div>

          <div className="legal-copy">

            <p>
              Some features of P-ARENA may rely on
              third-party service providers that
              support infrastructure, hosting,
              security, communications, or other
              technical services.
            </p>

            <p>
              Depending on the services used, those
              providers may use their own cookies
              or similar technologies in accordance
              with their applicable policies.
            </p>

            <p>
              If P-ARENA introduces additional
              third-party technologies that
              materially affect how cookies or
              similar technologies are used, this
              policy may be updated accordingly.
            </p>

          </div>

        </section>

        {/* ================================================= */}
        {/* USER CHOICES                                      */}
        {/* ================================================= */}

        <section
          id="choices"
          className="legal-section"
        >

          <div className="legal-section-header">

            <span className="legal-section-number">
              08
            </span>

            <div>
              <p>
                YOUR CONTROL
              </p>

              <h2>
                Managing Cookies &amp; Storage
              </h2>
            </div>

          </div>

          <div className="legal-copy">

            <p>
              Most modern browsers provide controls
              that allow users to review, block, or
              delete cookies and other stored
              website data.
            </p>

            <p>
              You may be able to manage these
              technologies through your browser's
              privacy, security, cookie, or site
              data settings.
            </p>

          </div>

          <div className="legal-rights-grid">

            <article>

              <strong>
                01
              </strong>

              <h3>
                View
              </h3>

              <p>
                Your browser may allow you to view
                cookies and site data stored by
                websites.
              </p>

            </article>

            <article>

              <strong>
                02
              </strong>

              <h3>
                Delete
              </h3>

              <p>
                You may be able to remove cookies,
                local storage, and other site data
                through your browser.
              </p>

            </article>

            <article>

              <strong>
                03
              </strong>

              <h3>
                Block
              </h3>

              <p>
                Browsers may provide settings for
                blocking some or all cookies.
              </p>

            </article>

            <article>

              <strong>
                04
              </strong>

              <h3>
                Reset
              </h3>

              <p>
                Clearing P-ARENA site data may
                reset certain preferences or
                application state.
              </p>

            </article>

          </div>

          <div className="legal-note">

            <strong>
              ◆
            </strong>

            <p>
              Blocking or deleting technologies
              required for authentication,
              security, or core functionality may
              cause some P-ARENA features to stop
              working correctly or may require you
              to sign in again.
            </p>

          </div>

        </section>

        {/* ================================================= */}
        {/* DO NOT TRACK                                      */}
        {/* ================================================= */}

        <section className="legal-section">

          <div className="legal-section-header">

            <span className="legal-section-number">
              09
            </span>

            <div>
              <p>
                BROWSER SIGNALS
              </p>

              <h2>
                Browser Privacy Signals
              </h2>
            </div>

          </div>

          <div className="legal-copy">

            <p>
              Browsers and devices may provide
              privacy-related settings or signals.
              Because standards and legal
              requirements concerning these
              signals may evolve, P-ARENA may
              update its practices as appropriate.
            </p>

            <p>
              Where applicable law requires
              recognition of a particular
              browser-based privacy signal,
              P-ARENA will seek to handle that
              signal as required.
            </p>

          </div>

        </section>

        {/* ================================================= */}
        {/* PRIVACY POLICY                                    */}
        {/* ================================================= */}

        <section className="legal-section">

          <div className="legal-section-header">

            <span className="legal-section-number">
              10
            </span>

            <div>
              <p>
                RELATED POLICY
              </p>

              <h2>
                Privacy Policy
              </h2>
            </div>

          </div>

          <div className="legal-copy">

            <p>
              Our use of cookies and similar
              technologies may involve information
              covered by the P-ARENA Privacy
              Policy.
            </p>

            <p>
              Please review our{" "}
              <Link
                to="/privacy"
                className="legal-inline-link"
              >
                Privacy Policy
              </Link>{" "}
              for additional information about how
              P-ARENA collects, uses, protects, and
              retains information.
            </p>

          </div>

        </section>

        {/* ================================================= */}
        {/* CHANGES                                           */}
        {/* ================================================= */}

        <section
          id="changes"
          className="legal-section"
        >

          <div className="legal-section-header">

            <span className="legal-section-number">
              11
            </span>

            <div>
              <p>
                UPDATES
              </p>

              <h2>
                Changes to This Cookie Policy
              </h2>
            </div>

          </div>

          <div className="legal-copy">

            <p>
              P-ARENA may update this Cookie Policy
              from time to time as the platform,
              technologies, or applicable legal
              requirements change.
            </p>

            <p>
              When this policy is updated, the
              "Last Updated" date displayed at the
              top of this page will be revised.
            </p>

            <p>
              We encourage you to review this
              Cookie Policy periodically to remain
              informed about how P-ARENA uses
              cookies and similar technologies.
            </p>

          </div>

        </section>

        {/* ================================================= */}
        {/* CONTACT                                           */}
        {/* ================================================= */}

        <section
          id="contact"
          className="legal-section"
        >

          <div className="legal-section-header">

            <span className="legal-section-number">
              12
            </span>

            <div>
              <p>
                QUESTIONS
              </p>

              <h2>
                Contact Us
              </h2>
            </div>

          </div>

          <div className="legal-contact-card">

            <div className="legal-contact-emblem">
              ♠ ♥ ♦ ♣
            </div>

            <small>
              P-ARENA
            </small>

            <h3>
              Cookie &amp; Privacy Questions
            </h3>

            <p>
              If you have questions about this
              Cookie Policy or P-ARENA's use of
              browser technologies, please contact
              us through the contact information
              provided on the platform.
            </p>

          </div>

        </section>

        {/* ================================================= */}
        {/* LEGAL NAVIGATION                                  */}
        {/* ================================================= */}

        <section className="legal-footer-navigation">

          <div className="legal-footer-suits">
            <span>♠</span>
            <span>♥</span>
            <span>♦</span>
            <span>♣</span>
          </div>

          <small>
            P-ARENA
          </small>

          <h2>
            Legal Information
          </h2>

          <p>
            Review the policies governing your use
            of P-ARENA.
          </p>

          <div className="legal-footer-links">

            <Link to="/privacy">
              Privacy Policy
            </Link>

            <Link to="/terms">
              Terms &amp; Conditions
            </Link>

            <Link
              to="/cookies"
              className="active"
            >
              Cookie Policy
            </Link>

          </div>

          <Link
            to="/"
            className="legal-back-home"
          >
            ← Back to Game House
          </Link>

        </section>

      </div>

    </main>
  );
};

export default CookiePolicy;